import React, { useState } from "react";
import { View } from "ui/atoms/view";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import { AddIcon, TrashIcon } from "ui/gluestack/icon";
import { Control } from "react-hook-form";
import {
  AssessmentEditFormModel,
  useAssessmentEdit,
} from "../context/AssessmentEditContext";
import {
  SectionQuestionsProvider,
  useSectionQuestions,
} from "../context/SectionQuestionsContext";
import QuestionEditModal from "../templates/QuestionEditModal";
import { QuestionRowMolecule } from "../molecules/QuestionRowMolecule";
import { Text } from "react-native";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";

interface Props {
  removeSection: () => void;
  control: Control<AssessmentEditFormModel>;
  sectionIndex: number;
}

export function AssessmentEditQuestionsOrganism({
  removeSection,
  control,
  sectionIndex,
}: Props) {
  return (
    <SectionQuestionsProvider control={control} sectionIndex={sectionIndex}>
      <QuestionsContent
        removeSection={removeSection}
        sectionIndex={sectionIndex}
      />
    </SectionQuestionsProvider>
  );
}

function QuestionsContent({
  removeSection,
  sectionIndex,
}: Omit<Props, "control">) {
  const { questionFields, appendQuestion } = useSectionQuestions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const { isEditing, hasEditingPermission } = useAssessmentEdit();
  return (
    <View>
      {questionFields.map((q, idx) => (
        <QuestionRowMolecule
          key={q.id}
          idx={idx}
          question={q}
          sectionIndex={sectionIndex}
        />
      ))}
      {hasEditingPermission && questionFields.length === 0 ? (
        <View className="flex items-center bg-gray-100 rounded mt-2">
          <Text className="text-center text-gray-500 py-4">
            Add a question to get started
          </Text>
        </View>
      ) : null}

      <PermissionCheck
        permission={
          isEditing
            ? PermissionsEnums.UPDATE_FORM
            : PermissionsEnums.CREATE_FORM
        }
      >
        <View className="flex flex-row justify-between pt-8">
          <Button
            size="sm"
            variant="outline"
            action="negative"
            onPress={removeSection}
          >
            <ButtonIcon as={TrashIcon} />
            <ButtonText>Delete section</ButtonText>
          </Button>
          <Button
            size="sm"
            variant="outline"
            action="primary"
            onPress={() => setIsCreateOpen(true)}
          >
            <ButtonText>Add question</ButtonText>
            <ButtonIcon as={AddIcon} />
          </Button>
        </View>
      </PermissionCheck>

      <QuestionEditModal
        isVisible={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={(payload) => {
          appendQuestion({
            id: undefined,
            content: payload.content!,
            type: payload.type!,
            required: !!payload.required,
            unit: payload.unit,
            min_value: payload.min_value ?? null,
            max_value: payload.max_value ?? null,
            options: payload.options ?? null,
            instructions: payload.instructions ?? [],
          });
          setIsCreateOpen(false);
        }}
      />
    </View>
  );
}
